import React, { useMemo, useState, useEffect } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import {
    MemberState,
    ZinantisRelationshipStatusTypeKeys,
} from '../../../../../types/zinantis/members/membersType';
import NavigationArrows from './NavigationArrows';
import 'keen-slider/keen-slider.min.css';
import '../../../../../stylesheets/stylesComponents/members-carousel.scss';
import { getCSSVariable } from '../../../../../stylesheets/cssVariablesUtils';
import MemberCard from '../../MemberCard';
import { useSearchParams } from '../../../../../customHooks/useSearchParams';

interface MemberSegmentProps {
    segment: string;
    members: MemberState[];
    visibleSegmentName: boolean;
    visibleNewMember?: boolean;
    totalMembersCount: number;
    headerButtonbar?: React.ReactNode;
}

const MemberSegment = (props: MemberSegmentProps) => {
    const {
        segment,
        members,
        visibleSegmentName,
        visibleNewMember = false,
        totalMembersCount,
        headerButtonbar,
    } = props;

    const [loaded, setLoaded] = useState(false);
    const [isInfiniteLoop, setIsInfiniteLoop] = useState(false);

    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        loop: isInfiniteLoop,
        initial: 0,
        slides: { perView: 1 },
        breakpoints: {
            [`(min-width: ${getCSSVariable('sm')})`]: {
                slides: { perView: 2, spacing: 5 },
            },
            [`(min-width: ${getCSSVariable('lg')})`]: {
                slides: { perView: 3, spacing: 10 },
            },
            [`(min-width: ${getCSSVariable('xl')})`]: {
                slides: { perView: 4, spacing: 10 },
            },
        },
        created() {
            setLoaded(true);
        },
    });

    const getSlidesPerView = () => {
        const width = window.innerWidth;
        if (width >= parseInt(getCSSVariable('xl'))) return 4;
        if (width >= parseInt(getCSSVariable('lg'))) return 3;
        if (width >= parseInt(getCSSVariable('sm'))) return 2;
        return 1;
    };

    // 🔹 Recalcular flechas y resetear carrusel cuando cambian los miembros
    useEffect(() => {
        if (instanceRef.current) {
            instanceRef.current.update();
            instanceRef.current.moveToIdx(0);
            const perView = getSlidesPerView();
            setIsInfiniteLoop(members.length > perView);
        }
    }, [members, instanceRef]);

    // 🔹 Recalcular también al redimensionar la ventana
    useEffect(() => {
        const handleResize = () => {
            const perView = getSlidesPerView();
            setIsInfiniteLoop(members.length > perView);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [members.length]);

    const membersCountText = useMemo(() => {
        if (members.length === totalMembersCount) return `(${members.length})`;
        return `(${members.length} / ${totalMembersCount})`;
    }, [members.length, totalMembersCount]);

    const { setSearchParam } = useSearchParams({ base64: true });

    const handleCardClick = (cardId: string) => {
        setSearchParam('id', cardId);
    };

    return (
        <div className='flex-column gap-10 padding-right10 carousel__wrapper'>
            <div className='flex-between flex-align-center'>
                <h5>
                    {segment}{' '}
                    <span style={{ fontWeight: 300, fontSize: '12px' }}>
                        {membersCountText}
                    </span>
                </h5>
                {headerButtonbar && headerButtonbar}
            </div>
            <div ref={sliderRef} className='keen-slider'>
                {members.map(member => (
                    <MemberCard
                        key={member.id}
                        member={member}
                        visibleSegmentName={visibleSegmentName}
                        visibleNewMember={visibleNewMember}
                        className={`keen-slider__slide ${
                            member.statusKey ===
                            ZinantisRelationshipStatusTypeKeys.DISCONNECTED
                                ? '--disconnected'
                                : ''
                        }`}
                        onClick={() => {
                            handleCardClick(member.id);
                        }}
                    />
                ))}
            </div>
            {loaded && instanceRef.current && isInfiniteLoop && (
                <NavigationArrows
                    onPrevClick={() => instanceRef.current?.prev()}
                    onNextClick={() => instanceRef.current?.next()}
                />
            )}
        </div>
    );
};

export default MemberSegment;
